'use client';

import {
  ControllerRenderProps,
  FieldValues,
  Path,
  PathValue,
  useForm
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  FieldConfig,
  OutputConfig,
  SectionConfig
} from '@/services/calculators/types/common';
import {
  CurrencyField,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Percent,
  TextField,
  ToggleGroup,
  Form,
  Button
} from '@baseline/ui';
import styles from './calculator.module.scss';
import CalculatorOutputCard from '@/components/Calculator/components/CalculatorOutputCard';
import { useEffect, useState } from 'react';

type CalculatorProps<T> = {
  schema: z.ZodType<T>;
  sectionConfig: SectionConfig<T>;
  getFieldConfig: (data: T) => FieldConfig<T>;
  getOutput: (data: T) => OutputConfig;
  keys: (keyof T)[];
};

export default function Calculator<T extends FieldValues>({
  schema,
  sectionConfig,
  getFieldConfig,
  getOutput,
  keys
}: CalculatorProps<T>) {
  const [showOutput, setShowOutput] = useState(false);
  const [outputData, setOutputData] = useState<OutputConfig>();
  const renderField = (
    rawField: FieldConfig<T>[keyof T],
    formField: ControllerRenderProps<T, Path<T>>
  ) => {
    switch (rawField.type) {
      case 'number':
        return (
          <TextField
            {...formField}
            id={formField.name}
            type={rawField.type}
            label={undefined}
            placeholder={rawField.placeholder}
            fieldSuffix={rawField.suffix}
          />
        );
      case 'percentage':
        return (
          <TextField
            {...formField}
            id={formField.name}
            label={undefined}
            placeholder={rawField.placeholder}
            fieldSuffix={<Percent />}
          />
        );
      case 'currency':
        return (
          <CurrencyField
            id={formField.name}
            name={formField.name}
            value={formField.value as number | undefined}
            onValueChange={(values) => formField.onChange(values.floatValue)}
            placeholder={rawField.placeholder}
            label={undefined}
          />
        );
      case 'toggle-group':
        return (
          <ToggleGroup
            {...formField}
            id={formField.name}
            options={rawField.options.map((option) => ({
              value: option.value,
              children: option.label
            }))}
            onValueChange={(value) => formField.onChange(value)}
            type="single"
          />
        );
      default:
        return <></>;
    }
  };

  const form = useForm<T>({
    resolver: zodResolver(schema)
  });

  useEffect(() => {
    if (!form.formState.isValid) {
      setShowOutput(false);
    }
  }, [form.formState.isValid]);

  const formData = form.watch();
  const fieldConfig = getFieldConfig(formData);

  return (
    <div className={styles.calculator}>
      <Form {...form}>
        <form className={styles.form}>
          {sectionConfig.map(({ fields, title }, index) => {
            return (
              <div className={styles.section} key={title || index}>
                {title && <h2 className={styles.sectionTitle}>{title}</h2>}
                {fields.map((fieldKey) => {
                  const field = fieldConfig[fieldKey];
                  if (field.hide) return null;
                  return (
                    <FormItem key={field.name as string}>
                      <div className={styles.field}>
                        {field.label && (
                          <label
                            className={styles.label}
                            htmlFor={field.name as string}
                          >
                            {field.label}
                          </label>
                        )}
                        <FormField
                          control={form.control}
                          name={field.name as Path<T>}
                          render={({ field: formField }) => (
                            <FormControl>
                              <div>
                                {renderField(field, formField)}
                                <FormMessage className={styles.errorMessage} />
                              </div>
                            </FormControl>
                          )}
                        />
                      </div>
                    </FormItem>
                  );
                })}
              </div>
            );
          })}
        </form>

        {showOutput && outputData && (
          <div className={styles.outputWrapper}>
            <div className={styles.output}>
              {outputData.map((section, index) => (
                <div className={styles.outputSection} key={index}>
                  {section.fields.map((outputField, idx) => (
                    <CalculatorOutputCard
                      key={idx}
                      label={outputField.label}
                      value={outputField.value}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className={styles.actionButtons}>
          <Button
            onClick={() => {
              setShowOutput(true);
              setOutputData(getOutput(formData));
            }}
            size="medium"
            disabled={!form.formState.isValid}
          >
            Calculate
          </Button>
          <Button
            colorScheme="tertiary"
            onClick={() => {
              keys.forEach((key) => {
                form.setValue(key as Path<T>, '' as PathValue<T, Path<T>>);
              });
              setShowOutput(false);
            }}
          >
            Clear
          </Button>
        </div>
      </Form>
    </div>
  );
}
